import { fetchTimeEntriesForDateRange } from '@/context/TaskContext/timeEntriesApi';

export const formatTime = (minutes: number) => {
  if (minutes < 60) return `${minutes}m`;
  const hours = minutes / 60;
  return `${hours.toFixed(1)}hr`;
};

export const minutesToSliderValue = (minutes: number) => {
  return Math.round(minutes / 30);
};

export const sliderValueToMinutes = (value: number) => {
  return value * 30;
};

export const calculateWeeklyProgress = async (userId: string | null, dailyTarget: number) => {
  const today = new Date();
  const startOfWeek = new Date(today);
  startOfWeek.setDate(today.getDate() - today.getDay());
  startOfWeek.setHours(0, 0, 0, 0);

  const endOfWeek = new Date(startOfWeek);
  endOfWeek.setDate(startOfWeek.getDate() + 6);
  endOfWeek.setHours(23, 59, 59, 999);

  // Fetch time entries for the week if user is available
  let timeEntries: any[] = [];
  if (userId) {
    try {
      timeEntries = await fetchTimeEntriesForDateRange(
        userId,
        startOfWeek.toISOString(),
        endOfWeek.toISOString()
      );
    } catch (error) {
      console.error('Error fetching time entries for weekly progress:', error);
      timeEntries = [];
    }
  }

  const dailyProgress = Array.from({ length: 7 }, (_, i) => {
    const date = new Date(startOfWeek);
    date.setDate(startOfWeek.getDate() + i);
    date.setHours(0, 0, 0, 0);
    
    const nextDay = new Date(date);
    nextDay.setDate(date.getDate() + 1);
    
    // Calculate focus time from time entries for this day
    const dayEntries = timeEntries.filter(entry => {
      const entryDate = new Date(entry.logged_at);
      return entryDate >= date && entryDate < nextDay;
    });
    
    const focusTime = dayEntries.reduce((total, entry) => total + entry.duration_seconds, 0);
    const focusMinutes = Math.floor(focusTime / 60);
    
    return {
      day: date.toLocaleDateString('en-US', { weekday: 'short' }),
      minutes: focusMinutes,
      target: dailyTarget,
      isToday: date.toDateString() === today.toDateString(),
      isPast: date < today
    };
  });

  return dailyProgress;
};

// Fallback function for backward compatibility when tasks are used
export const calculateWeeklyProgressFromTasks = (tasks: any[], dailyTarget: number) => {
  const today = new Date();
  const startOfWeek = new Date(today);
  startOfWeek.setDate(today.getDate() - today.getDay());
  startOfWeek.setHours(0, 0, 0, 0);

  const dailyProgress = Array.from({ length: 7 }, (_, i) => {
    const date = new Date(startOfWeek);
    date.setDate(startOfWeek.getDate() + i);
    date.setHours(0, 0, 0, 0);
    
    const nextDay = new Date(date);
    nextDay.setDate(date.getDate() + 1);
    
    const dayTasks = tasks.filter(task => {
      const taskDate = new Date(task.created_at);
      return taskDate >= date && taskDate < nextDay;
    });
    
    const focusTime = dayTasks.reduce((total, task) => total + (task.total_time_spent || 0), 0);
    const focusMinutes = Math.floor(focusTime / 60);
    
    return {
      day: date.toLocaleDateString('en-US', { weekday: 'short' }),
      minutes: focusMinutes,
      target: dailyTarget,
      isToday: date.toDateString() === today.toDateString(),
      isPast: date < today
    };
  });

  return dailyProgress;
};

export const calculateMonthlyProgress = async (userId: string | null, currentDate: Date, dailyTarget: number) => {
  const year = currentDate.getFullYear();
  const month = currentDate.getMonth();
  
  const startOfMonth = new Date(year, month, 1);
  startOfMonth.setHours(0, 0, 0, 0);
  
  const endOfMonth = new Date(year, month + 1, 0);
  endOfMonth.setHours(23, 59, 59, 999);

  // Fetch time entries for the month if user is available
  let timeEntries: any[] = [];
  if (userId) {
    try {
      timeEntries = await fetchTimeEntriesForDateRange(
        userId,
        startOfMonth.toISOString(),
        endOfMonth.toISOString()
      );
    } catch (error) {
      console.error('Error fetching time entries for monthly progress:', error);
      timeEntries = [];
    }
  }

  const daysInMonth = endOfMonth.getDate();
  const monthlyData = [];

  for (let day = 1; day <= daysInMonth; day++) {
    const date = new Date(year, month, day);
    date.setHours(0, 0, 0, 0);
    
    const nextDay = new Date(date);
    nextDay.setDate(date.getDate() + 1);
    
    // Calculate focus time from time entries for this day
    const dayEntries = timeEntries.filter(entry => {
      const entryDate = new Date(entry.logged_at);
      return entryDate >= date && entryDate < nextDay;
    });
    
    const focusTime = dayEntries.reduce((total, entry) => total + entry.duration_seconds, 0);
    const focusMinutes = Math.floor(focusTime / 60);
    
    monthlyData.push({
      day,
      minutes: focusMinutes,
      achieved: focusMinutes >= dailyTarget,
      date: date.toISOString()
    });
  }

  return monthlyData;
};

export const calculateChartDomain = (dailyProgress: any[], dailyTarget: number) => {
  const maxProgress = Math.max(...dailyProgress.map(day => day.minutes));
  const yAxisMax = Math.max(dailyTarget, maxProgress);
  const roundedMax = Math.ceil(yAxisMax / 30) * 30;
  
  // Only show meaningful ticks: 30m, full hours between 30m and goal, and the goal
  const yAxisTicks = [30]; // Always start with 30m
  
  // Add full hours between 60 and roundedMax
  for (let i = 60; i < roundedMax; i += 60) {
    yAxisTicks.push(i);
  }
  
  // Add the top goal time if it's not already included
  if (!yAxisTicks.includes(roundedMax)) {
    yAxisTicks.push(roundedMax);
  }

  return { roundedMax, yAxisTicks };
};
